import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { ArrowLeft, Save, RotateCcw } from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

// convert "yyyy-mm-dd" from <input type="date"> to "dd/MM/yyyy"
function ymdToDDMMYYYY(ymd) {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  if (!y || !m || !d) return "";
  return `${d}/${m}/${y}`;
}

// convert "dd/MM/yyyy" to "yyyy-mm-dd" for <input type="date">
function ddmmyyyyToYmd(ddmmyyyy) {
  if (!ddmmyyyy) return "";
  const parts = ddmmyyyy.split("/");
  if (parts.length !== 3) return "";
  const [dd, mm, yyyy] = parts;
  if (!dd || !mm || !yyyy) return "";
  const d2 = dd.padStart(2, "0");
  const m2 = mm.padStart(2, "0");
  return `${yyyy}-${m2}-${d2}`;
}

export default function EmployeeLicenseRenewalEntry() {
  const nav = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const finYearId = localStorage.getItem("finYearId") || "";
  const employeeId = localStorage.getItem("licempid") || ""; // set from listing click

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // display fields
  const [empCode, setEmpCode] = useState("");
  const [empName, setEmpName] = useState("");
  const [designation, setDesignation] = useState("");
  const [branchName, setBranchName] = useState("");

  // editable fields
  const [dled, setDled] = useState(""); // store as dd/MM/yyyy (like ASP)
  const [drivingLicNo, setDrivingLicNo] = useState("");
  const [otherExpDate, setOtherExpDate] = useState(""); // dd/MM/yyyy

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const screenBadge = useMemo(() => "INFRA SCREEN : C-8/1", []);

  const loadDetails = async () => {
    if (!finYearId) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!employeeId) {
      toast.warn("Employee not selected. Go back to listing.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(
        `${BASE_URL}/api/employee-license-renewal-entry/details`,
        {
          params: {
            finYearId: Number(finYearId),
            employeeId: Number(employeeId),
          },
        },
      );

      const row = res?.data?.row;
      if (!row) {
        toast.warn("No data found for selected employee.");
        return;
      }

      setEmpCode(row.fld_Employeecode ?? row.fld_employeecode ?? "");
      setEmpName(row.fld_Employee ?? row.fld_employee ?? "");
      setDesignation(row.fld_designationname ?? "");
      setBranchName(row.fld_branchname ?? "");

      // ASP fills textboxes with string returned by DB (could be dd/MM/yyyy or something)
      // We will accept either dd/MM/yyyy or yyyy-mm-dd; normalize if possible.
      const dledDb = (row.fld_drivinglicexpdate ?? "").toString();
      const otherDb = (row.fld_otherexpirydate ?? "").toString();

      // if already dd/MM/yyyy keep, else if yyyy-mm-dd convert
      setDled(
        dledDb.includes("-") ? ymdToDDMMYYYY(dledDb.slice(0, 10)) : dledDb,
      );
      setOtherExpDate(
        otherDb.includes("-") ? ymdToDDMMYYYY(otherDb.slice(0, 10)) : otherDb,
      );

      setDrivingLicNo(row.fld_drivinglicnum ?? "");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load details",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onReset = () => {
    setDled("");
    setDrivingLicNo("");
    setOtherExpDate("");
  };

  const onSave = async () => {
    if (!finYearId) return toast.warn("Please select Financial Year.");
    if (!employeeId) return toast.warn("Employee not selected.");

    try {
      setSaving(true);

      const payload = {
        finYearId: Number(finYearId),
        employeeId: Number(employeeId),
        drivingLicNo: drivingLicNo.trim(),
        dled: dled.trim(), // dd/MM/yyyy or ""
        otherExpDate: otherExpDate.trim(), // dd/MM/yyyy or ""
      };

      const res = await axios.post(
        `${BASE_URL}/api/employee-license-renewal-entry/update`,
        payload,
      );

      if (res?.data?.success) {
        toast.success("Updated Successfully");
        localStorage.removeItem("licempid"); // same as Session.Remove("licempid")
        nav("/payroll/employee-license-renewal/listing");
      } else {
        toast.error(res?.data?.message || "Update failed");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Update failed",
      );
    } finally {
      setSaving(false);
    }
  };

  const backToList = () => {
    localStorage.removeItem("licempid");
    nav("/payroll/EmployeeLicenseRenewal");
  };

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              EMPLOYEE LICENSE RENEWAL - ENTRY
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />
        <button
          onClick={backToList}
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
            isDark
              ? "border-slate-600 text-slate-300 hover:bg-slate-800"
              : "border-slate-300 text-slate-700 hover:bg-slate-100"
          }`}
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <div
          className={`mx-auto max-w-5xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-3">
              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}
              >
                {screenBadge}
              </div>
            </div>
          </div>

          <div className={cardBase + " p-6"}>
            {/* Top details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className={labelBase}>Employee Code</label>
                <div
                  className={`${inputBase} mt-2 !bg-transparent`}
                  style={{ pointerEvents: "none" }}
                >
                  {loading ? "Loading..." : empCode || "-"}
                </div>
              </div>

              <div>
                <label className={labelBase}>Employee Name</label>
                <div
                  className={`${inputBase} mt-2 !bg-transparent`}
                  style={{ pointerEvents: "none" }}
                >
                  {loading ? "Loading..." : empName || "-"}
                </div>
              </div>

              <div>
                <label className={labelBase}>Designation</label>
                <div
                  className={`${inputBase} mt-2 !bg-transparent`}
                  style={{ pointerEvents: "none" }}
                >
                  {loading ? "Loading..." : designation || "-"}
                </div>
              </div>

              <div>
                <label className={labelBase}>Branch</label>
                <div
                  className={`${inputBase} mt-2 !bg-transparent`}
                  style={{ pointerEvents: "none" }}
                >
                  {loading ? "Loading..." : branchName || "-"}
                </div>
              </div>
            </div>

            <div className="h-px bg-gray-200 dark:bg-gray-700 my-6" />

            {/* Editable fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className={labelBase}>
                  Driving License Expiry Date (DLED)
                </label>
                {/* use date picker but store as dd/MM/yyyy (ASP style) */}
                <input
                  type="date"
                  value={ddmmyyyyToYmd(dled)}
                  onChange={(e) => setDled(ymdToDDMMYYYY(e.target.value))}
                  className={inputBase + " mt-2"}
                />
                <div
                  className={`mt-2 text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
                >
                  Saved format: dd/MM/yyyy
                </div>
              </div>

              <div>
                <label className={labelBase}>Driving License No</label>
                <input
                  value={drivingLicNo}
                  onChange={(e) => setDrivingLicNo(e.target.value)}
                  className={inputBase + " mt-2"}
                  placeholder="Enter license number"
                />
              </div>

              <div>
                <label className={labelBase}>Other Expiry Date</label>
                <input
                  type="date"
                  value={ddmmyyyyToYmd(otherExpDate)}
                  onChange={(e) =>
                    setOtherExpDate(ymdToDDMMYYYY(e.target.value))
                  }
                  className={inputBase + " mt-2"}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-3 justify-center mt-8">
              <button
                onClick={onSave}
                className={primaryBtn}
                disabled={saving || loading}
              >
                <Save className="w-4 h-4" />
                {saving ? "Saving..." : "Save"}
              </button>

              <button
                onClick={onReset}
                className={ghostBtn}
                disabled={saving || loading}
              >
                <RotateCcw className="w-4 h-4" />
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
}
